import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ExploreServiceState, updateExploreActionType, updateFilters, updateSort, updateTrailEngagement } from '../redux/exploreServiceSlice';
import ExploreServiceFilters from '../types/ExploreServiceFilters';
import ExploreServiceSort from '../types/ExploreServiceSort';
import TrailEngagement from '../types/TrailEngagement';

/**
 * A convenience hook for interacting with the basic explore service filter state to reduce code duplication.
 * Distance away is not included since different places handle it differently.
 * @returns values and update functions for basic explore service filters
 */
const useExploreServiceFilterState = () => {
  const dispatch = useDispatch();
  const { exploreServiceFilters, exploreServiceSort, trailEngagement } = useSelector((state: { exploreService: ExploreServiceState }) => ({
    exploreServiceFilters: state.exploreService.filters,
    exploreServiceSort: state.exploreService.sort,
    trailEngagement: state.exploreService.trailEngagement
  }));

  // Just used to compare the new filters to the old filters to determine if the filters changed.
  const previousFilters = useRef(exploreServiceFilters);
  useEffect(() => {
    previousFilters.current = exploreServiceFilters;
  }, [exploreServiceFilters]);

  const onFiltersChange = useCallback(
    (newFilters: ExploreServiceFilters) => {
      if (JSON.stringify(previousFilters.current) === JSON.stringify(newFilters)) {
        return;
      }

      if (Object.keys(previousFilters.current).length > Object.keys(newFilters).length) {
        dispatch(updateExploreActionType('clear filter'));
      } else {
        dispatch(updateExploreActionType('filter applied'));
      }

      dispatch(updateFilters(newFilters));
    },
    [dispatch]
  );

  // Just used to compare the new sort to the old sort to determine if sort was changed.
  const previousSort = useRef(exploreServiceSort);
  useEffect(() => {
    previousSort.current = exploreServiceSort;
  }, [exploreServiceSort]);

  const onSortChange = useCallback(
    (sort: ExploreServiceSort) => {
      if (previousSort.current === sort) {
        return;
      }

      dispatch(updateExploreActionType('filter applied'));
      dispatch(updateSort(sort));
    },
    [dispatch]
  );

  // Just used to compare the new trail engagement to the old trail engagement to determine if trail engagement changed.
  const previousTrailEngagement = useRef(trailEngagement);
  useEffect(() => {
    previousTrailEngagement.current = trailEngagement;
  }, [trailEngagement]);

  const onTrailEngagementChanged = useCallback(
    (trailEngagement: TrailEngagement[]) => {
      if (JSON.stringify(previousTrailEngagement.current) === JSON.stringify(trailEngagement)) {
        return;
      }

      if (trailEngagement.length) {
        dispatch(updateExploreActionType('filter applied'));
      } else {
        dispatch(updateExploreActionType('clear filter'));
      }

      dispatch(updateTrailEngagement(trailEngagement));
    },
    [dispatch]
  );

  return useMemo(
    () => ({
      exploreServiceFilters,
      onFiltersChange,
      exploreServiceSort,
      onSortChange,
      trailEngagement,
      onTrailEngagementChanged
    }),
    [exploreServiceFilters, onFiltersChange, exploreServiceSort, onSortChange, trailEngagement, onTrailEngagementChanged]
  );
};

export default useExploreServiceFilterState;
